import { apiClient } from './apiClient';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  durationDays: number;
  billingCycle: string;
  features: string[];
  maxUsers?: number;
  maxDoctors?: number;
  maxAnimals?: number;
  maxAppointments?: number;
  isActive: boolean;
  target_type: string;
  created_at?: string;
  updated_at?: string;
  stripePriceId?: string;
  allowManualClinicActivation?: boolean;
}

export interface ClinicSubscriptionDto {
  id: string;
  userId?: string | null;
  clinicId?: string | null;
  planId: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  nextBillingDate: string | null;
  amount: number;
  currency: string;
  autoRenew: boolean;
  cancelAtPeriodEnd: boolean;
  scheduledPlanId: string | null;
  stripeSubscriptionId?: string | null;
  stripeCustomerId?: string | null;
  providerCustomerId?: string | null;
  providerSubscriptionId?: string | null;
  providerPriceId?: string | null;
  billingMode: string;
  cancelledAt: string | null;
  cancellationReason?: string | null;
  plan?: SubscriptionPlan;
  planName?: string;
}

export interface ClinicEntitlementsDto {
  hasActiveSubscription: boolean;
  subscriptionId?: string;
  status?: string;
  limits: {
    maxUsers: number;
    maxDoctors: number;
    maxPatients: number;
    maxAppointments: number;
  } | null;
  featureCodes: string[];
  planFeatures: string[];
  premiumCapable: boolean;
}

function parseFeatures(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try {
      const j = JSON.parse(raw);
      return Array.isArray(j) ? j.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizePlan(row: Record<string, unknown>): SubscriptionPlan {
  return {
    id: String(row.id),
    name: String(row.name || ''),
    description: row.description != null ? String(row.description) : undefined,
    price: Number(row.price),
    currency: String(row.currency || 'RON'),
    durationDays: Number(row.durationDays),
    billingCycle: String(row.billingCycle),
    features: parseFeatures(row.features),
    maxUsers: row.maxUsers != null ? Number(row.maxUsers) : undefined,
    maxDoctors: row.maxDoctors != null ? Number(row.maxDoctors) : undefined,
    maxAnimals: row.maxAnimals != null ? Number(row.maxAnimals) : undefined,
    maxAppointments: row.maxAppointments != null ? Number(row.maxAppointments) : undefined,
    isActive: Boolean(row.isActive),
    target_type: String(row.target_type || 'clinic'),
    stripePriceId: row.stripePriceId != null && String(row.stripePriceId) !== '' ? String(row.stripePriceId) : undefined,
    allowManualClinicActivation: Boolean(row.allowManualClinicActivation),
  };
}

function apiErr(e: unknown): string {
  if (typeof e === 'object' && e !== null && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string } } }).response;
    const msg = r?.data?.error;
    if (msg) return msg;
  }
  if (e instanceof Error) return e.message;
  return 'Eroare necunoscuta';
}

class SubscriptionService {
  async getPlans(targetType: 'clinic' | 'user' = 'user'): Promise<SubscriptionPlan[]> {
    try {
      const { data } = await apiClient.get<{ success: boolean; data?: unknown[]; error?: string }>('/subscriptions/plans', {
        params: { target_type: targetType },
      });
      if (!data.success || !data.data) {
        throw new Error(data.error || 'Eroare la obtinerea planurilor');
      }
      return (data.data as Record<string, unknown>[]).map(normalizePlan);
    } catch (e) {
      throw new Error(apiErr(e));
    }
  }

  async getActiveSubscription(): Promise<ClinicSubscriptionDto | null> {
    try {
      const { data } = await apiClient.get<{ success: boolean; data?: unknown[] }>('/subscriptions');
      if (!data.success || !data.data || !Array.isArray(data.data)) return null;
      const active = (data.data as Record<string, unknown>[]).find((s) => s.status === 'active' || s.status === 'trialing' || s.status === 'past_due');
      if (!active) return null;
      return active as unknown as ClinicSubscriptionDto;
    } catch (e) {
      console.error('Error fetching active subscription:', e);
      return null;
    }
  }

  async getClinicCurrent(): Promise<{ subscription: ClinicSubscriptionDto | null }> {
    const { data } = await apiClient.get<{ success: boolean; data: { subscription: ClinicSubscriptionDto | null }; error?: string }>(
      '/subscriptions/current'
    );
    if (!data.success) throw new Error(data.error || 'Eroare');
    return data.data;
  }

  async getClinicHistory(): Promise<{
    subscriptions: ClinicSubscriptionDto[];
    events: Array<{ id: string; subscriptionId: string; eventType: string; payload: unknown; createdAt: string | null }>;
  }> {
    const { data } = await apiClient.get<{ success: boolean; data: any; error?: string }>('/subscriptions/history');
    if (!data.success) throw new Error(data.error || 'Eroare');
    return data.data;
  }

  async getClinicEntitlements(): Promise<ClinicEntitlementsDto> {
    const { data } = await apiClient.get<{ success: boolean; data: ClinicEntitlementsDto; error?: string }>('/subscriptions/entitlements');
    if (!data.success) throw new Error(data.error || 'Eroare');
    return data.data;
  }

  async openStripeCustomerPortal(clinicId?: string): Promise<{ portalUrl: string }> {
    try {
      const { data } = await apiClient.post<{
        success: boolean;
        data: { portalUrl: string };
        error?: string;
      }>('/subscriptions/stripe/portal', clinicId ? { clinicId } : {});
      if (!data.success || !data.data?.portalUrl) {
        throw new Error(data.error || 'Nu s-a putut deschide Customer Portal');
      }
      return { portalUrl: data.data.portalUrl };
    } catch (e) {
      throw new Error(apiErr(e));
    }
  }

  async subscribeClinic(
    planId: string,
    billingMode: 'manual' | 'online' = 'manual',
    opts?: { successUrl?: string; cancelUrl?: string }
  ): Promise<
    | { subscription: ClinicSubscriptionDto; checkoutUrl?: undefined }
    | { subscription: ClinicSubscriptionDto; checkoutUrl: string | null }
  > {
    try {
      const { data } = await apiClient.post<{
        success: boolean;
        data: { subscription: ClinicSubscriptionDto; checkoutUrl?: string | null };
        error?: string;
      }>('/subscriptions/subscribe', {
        planId,
        billingMode,
        successUrl: opts?.successUrl,
        cancelUrl: opts?.cancelUrl,
      });
      if (!data.success || !data.data?.subscription) throw new Error(data.error || 'Eroare subscribe');
      if (data.data.checkoutUrl != null) {
        return { subscription: data.data.subscription, checkoutUrl: data.data.checkoutUrl };
      }
      return { subscription: data.data.subscription };
    } catch (e) {
      throw new Error(apiErr(e));
    }
  }

  async changeClinicPlan(newPlanId: string, effective: 'immediate' | 'period_end'): Promise<{ subscription: ClinicSubscriptionDto; scheduledChange: boolean }> {
    try {
      const { data } = await apiClient.patch<{
        success: boolean;
        data: { subscription: ClinicSubscriptionDto; scheduledChange: boolean };
        error?: string;
      }>('/subscriptions/change-plan', { newPlanId, effective });
      if (!data.success || !data.data) throw new Error(data.error || 'Eroare schimbare plan');
      return data.data;
    } catch (e) {
      throw new Error(apiErr(e));
    }
  }

  async cancelClinicSubscription(atPeriodEnd: boolean, reason?: string): Promise<ClinicSubscriptionDto> {
    try {
      const { data } = await apiClient.post<{ success: boolean; data: { subscription: ClinicSubscriptionDto }; error?: string }>(
        '/subscriptions/cancel',
        { atPeriodEnd, reason }
      );
      if (!data.success || !data.data?.subscription) throw new Error(data.error || 'Eroare anulare');
      return data.data.subscription;
    } catch (e) {
      throw new Error(apiErr(e));
    }
  }

  async reactivateClinicSubscription(): Promise<ClinicSubscriptionDto> {
    try {
      const { data } = await apiClient.post<{ success: boolean; data: { subscription: ClinicSubscriptionDto }; error?: string }>(
        '/subscriptions/reactivate',
        {}
      );
      if (!data.success || !data.data?.subscription) throw new Error(data.error || 'Eroare reactivare');
      return data.data.subscription;
    } catch (e) {
      throw new Error(apiErr(e));
    }
  }

  /** Legacy client user flow – rămâne pentru compatibilitate. */
  async createSubscription(planId: string, userId?: string, clinicId?: string, autoRenew: boolean = true): Promise<unknown> {
    const { data } = await apiClient.post('/subscriptions', { planId, userId, clinicId, autoRenew });
    if (!data.success) throw new Error((data as { error?: string }).error || 'Eroare');
    return (data as { data: unknown }).data;
  }

  async cancelSubscription(subscriptionId: string, reason?: string): Promise<boolean> {
    const { data } = await apiClient.post(`/subscriptions/${subscriptionId}/cancel`, { reason });
    return data.success === true;
  }
}

export default new SubscriptionService();
